/**
 * Feb 2026 · /enquire/:wsId  and  /enquire/:wsId/:typeSlug
 * Public enquiry intake widget — warm-luxe style consistent with the
 * studio booking widget. Supports a type-agnostic entry (customer
 * picks type) and a per-type deep-link (type pre-selected + hidden).
 * Style is layered: default → per-type page_config → props.styleOverride.
 */
import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";

const cream100 = "#FBF7F0", cream200 = "#F3ECE0", bark800 = "#241A11",
       bark600 = "#5C4A38", gold = "#B8860B";
const serifFamily = `"Cormorant Garamond", Georgia, serif`;
const sansFamily = `-apple-system, BlinkMacSystemFont, "Segoe UI", Inter, Roboto, sans-serif`;
const monoFamily = `"JetBrains Mono", "Fira Code", ui-monospace, monospace`;

const DEFAULTS = {
  hero_image_url: "", logo_url: "", tagline: "",
  welcome_html: "", thank_you_html: "", terms_html: "",
  cta_label: "Send enquiry →",
  primary_color: gold, accent_color: bark800,
  background_color: cream100, text_color: bark800,
  font_family: "serif",
  require_phone: false, require_company: false, require_budget: false,
  require_message: false, show_workspace_name: true, show_powered_by: true,
  // Feb 2026 · Compact/embed mode — hides the hero band so the form
  // can be dropped into a landing page as a slim, self-contained card
  // without the marketing header.
  hide_hero: false,
};

function _resolveFont(k) {
  if (k === "sans") return sansFamily;
  if (k === "mono") return monoFamily;
  return serifFamily;
}
function _abs(url) {
  if (!url) return "";
  if (/^https?:\/\//.test(url)) return url;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${url}`;
}

const styles = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&display=swap');
.eq-input { padding: 12px; border: 1px solid ${cream200}; background: white; font-size: 15px; transition: border-color 0.2s ease; scroll-margin-top: 24px; width: 100%; box-sizing: border-box; }
.eq-input:focus { outline: 0; border-color: var(--eq-primary, ${gold}); box-shadow: 0 0 0 3px rgba(184,134,11,0.15); }
.eq-type { padding: 20px; border: 1px solid ${cream200}; background: white; text-align: left; cursor: pointer; transition: all 0.24s cubic-bezier(0.4,0,0.2,1); }
.eq-type:hover { transform: translateY(-3px); box-shadow: 0 16px 32px -14px rgba(36,26,17,0.2); border-color: var(--eq-primary, ${gold}); }
.eq-type[data-active="1"] { border-color: var(--eq-primary, ${gold}); border-left-width: 3px; }
.eq-hero-h1 { line-height: 1.1; padding-bottom: 6px; }
.eq-welcome-html p { margin: 0 0 12px; line-height: 1.55; }
.eq-terms-html { font-size: 11px; color: #666; line-height: 1.5; }
.eq-tick { animation: eqPop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
@keyframes eqPop { from { opacity: 0; transform: scale(0.6); } to { opacity: 1; transform: scale(1); } }
.eq-btn-primary { transition: all 0.24s ease; letter-spacing: 2px; text-transform: uppercase; font-weight: 700; font-size: 12px; }
.eq-btn-primary:hover { transform: translateY(-1px); box-shadow: 0 12px 24px -8px rgba(36,26,17,0.28); }
@media (max-width: 640px) {
  .eq-hero-h1 { font-size: 32px !important; }
  .eq-card { padding: 20px 16px !important; }
}
`;

export default function EnquiryWidget({ styleOverride, sourceLabel, submitEndpoint }) {
  const { wsId, typeSlug } = useParams();
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    if (!wsId) return;
    const url = typeSlug
      ? `/public/enquiries/${wsId}/config?type_slug=${typeSlug}`
      : `/public/enquiries/${wsId}/config`;
    api.get(url)
       .then((r) => setCfg(r.data))
       .catch((e) => toast.error(apiErrorMessage(e, "Load failed")));
  }, [wsId, typeSlug]);

  return <EnquiryWidgetView cfg={cfg} wsId={wsId}
                              styleOverride={styleOverride}
                              sourceLabel={sourceLabel || (typeSlug ? "page" : "widget")}
                              submitEndpoint={submitEndpoint} />;
}


// Shared renderer — used by both the workspace-scoped widget and the
// slug-scoped multi-type widget (`/enquire-w/:slug`).
export function EnquiryWidgetView({ cfg, wsId, styleOverride, sourceLabel, submitEndpoint }) {
  const [typeId, setTypeId] = useState(null);
  const [form, setForm] = useState({
    subtype_slug: "", customer_name: "", customer_email: "",
    customer_phone: "", company: "", budget: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (cfg?.pinned && cfg.types?.[0]) setTypeId(cfg.types[0].id);
    else if (!cfg?.pinned && cfg?.types?.length === 1) setTypeId(cfg.types[0].id);
  }, [cfg]);

  const currentType = cfg?.types?.find((t) => t.id === typeId);

  // Style layers: DEFAULTS → per-type page_config (when pinned or picked) → widget override
  const style = useMemo(() => {
    const layers = { ...DEFAULTS };
    if (currentType?.page_config) Object.assign(layers, _stripEmpty(currentType.page_config));
    if (styleOverride) Object.assign(layers, _stripEmpty(styleOverride));
    return layers;
  }, [currentType, styleOverride]);
  const primary = style.primary_color || gold;
  const bg = style.background_color || cream100;
  const accent = style.accent_color || bark800;
  const text = style.text_color || bark800;
  const fontFamily = _resolveFont(style.font_family);

  const submit = async () => {
    if (!form.customer_name.trim()) { toast.error("Name is required"); return; }
    if (!form.customer_email.trim() && !form.customer_phone.trim()) {
      toast.error("Add an email or phone so we can reach you"); return;
    }
    if (!typeId) { toast.error("Pick an enquiry type"); return; }
    if (style.require_phone && !form.customer_phone.trim()) { toast.error("Phone is required"); return; }
    if (style.require_company && !form.company.trim()) { toast.error("Company is required"); return; }
    if (style.require_budget && !form.budget.trim()) { toast.error("Budget is required"); return; }
    if (style.require_message && !form.message.trim()) { toast.error("Message is required"); return; }
    setBusy(true);
    try {
      const body = {
        type_id: typeId,
        subtype_slug: form.subtype_slug || null,
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim(),
        customer_phone: form.customer_phone.trim(),
        company: form.company.trim(),
        budget: form.budget.trim(),
        message: form.message.trim(),
        create_lead: true,
        source: sourceLabel || "widget",
      };
      const endpoint = submitEndpoint || `/public/enquiries/${wsId}`;
      const { data } = await api.post(endpoint, body);
      setDone(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Submit failed")); }
    finally { setBusy(false); }
  };

  const resetForForNewEnquiry = () => {
    setDone(null);
    setForm({
      subtype_slug: "", customer_name: "", customer_email: "",
      customer_phone: "", company: "", budget: "", message: "",
    });
    // Keep typeId when pinned so single-type widgets stay on the same
    // form; otherwise let the user pick again.
    if (!cfg?.pinned && (cfg?.types?.length || 0) > 1) setTypeId(null);
    // Scroll back to the form.
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch {}
  };

  if (!cfg) return <div style={{ padding: 40, fontFamily }}>Loading…</div>;

  if (done) return (
    <div style={{ minHeight: "100vh", background: `linear-gradient(180deg, ${bg} 0%, #FFFFFF 100%)`, fontFamily, color: text }}>
      <style>{styles}</style>
      <div style={{ maxWidth: 600, margin: "0 auto", padding: "72px 24px 96px", textAlign: "center" }} data-testid="enq-widget-done">
        {/* Feb 2026 · R280.6 · Warmer thank-you: bigger success ring
            with a pulse, a workspace/type-specific opener line, a
            copy-friendly reference card, a hint about what happens
            next, AND a "Submit another" CTA + a link back to the
            picker when there are multiple types. */}
        <div style={{ width: 92, height: 92, borderRadius: "50%",
                        background: `radial-gradient(circle at 30% 30%, ${primary}22, ${cream200})`,
                        border: `1.5px solid ${primary}`, color: primary, fontSize: 44,
                        margin: "0 auto",
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        boxShadow: `0 20px 40px -20px ${primary}55` }}
              className="eq-tick">✓</div>
        <h1 style={{ fontFamily: serifFamily, fontSize: 44, margin: "22px 0 6px", fontWeight: 500, letterSpacing: -0.5 }}>
          Enquiry received — thank you!
        </h1>
        <p style={{ fontSize: 15, color: bark600, margin: "0 0 24px", lineHeight: 1.55 }}>
          Hi {form.customer_name?.split(" ")[0] || "there"}, we've captured your{" "}
          {(currentType?.name || "enquiry").toLowerCase()} request and our team is on it.
          You'll hear from us on <b style={{ color: text }}>{form.customer_email || form.customer_phone || "your contact"}</b>.
        </p>

        {/* Reference card */}
        <div style={{ display: "inline-block", padding: "14px 22px",
                        background: cream100, border: `1px solid ${cream200}`, marginBottom: 24 }}>
          <div style={{ fontSize: 10, letterSpacing: 3, textTransform: "uppercase",
                          color: bark600, fontWeight: 700 }}>Reference</div>
          <div style={{ fontFamily: monoFamily, fontSize: 24, fontWeight: 700, color: text,
                          marginTop: 4, letterSpacing: 1 }} data-testid="enq-widget-done-ref">{done.code}</div>
        </div>

        {/* Timeline */}
        <div style={{ maxWidth: 460, margin: "0 auto 28px", padding: "16px 20px",
                        background: "#FFFFFF", border: `1px solid ${cream200}`, textAlign: "left" }}>
          <div style={{ fontSize: 10, letterSpacing: 3, textTransform: "uppercase",
                          color: primary, fontWeight: 700, marginBottom: 10 }}>What happens next</div>
          <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: bark600, lineHeight: 1.7 }}>
            <li>Our team reviews your brief — usually within 1 business hour.</li>
            <li>We reach out with clarifying questions or a tailored quote.</li>
            <li>Once you're happy, we lock in the shoot / project timeline.</li>
          </ol>
        </div>

        {style.thank_you_html && (
          <div dangerouslySetInnerHTML={{ __html: style.thank_you_html }}
                style={{ color: bark600, marginBottom: 20 }} />
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", marginTop: 8 }}>
          <button onClick={resetForForNewEnquiry}
                    data-testid="enq-widget-done-new"
                    className="eq-btn-primary"
                    style={{ background: primary, color: "#FFFFFF", border: `1px solid ${primary}`,
                              padding: "12px 28px", cursor: "pointer" }}>
            Submit another enquiry →
          </button>
          {done.tracking_url && (
            <a href={done.tracking_url} target="_blank" rel="noopener noreferrer"
                data-testid="enq-widget-done-track"
                style={{ display: "inline-block", padding: "12px 24px",
                          border: `1px solid ${cream200}`, color: text,
                          fontSize: 12, letterSpacing: 2, textTransform: "uppercase",
                          fontWeight: 700, textDecoration: "none", background: "#FFFFFF" }}>
              Track this enquiry
            </a>
          )}
        </div>

        {style.show_powered_by !== false && (
          <div style={{ marginTop: 44, fontSize: 10, letterSpacing: 3, textTransform: "uppercase",
                          color: "#8A7A66" }}>
            Powered by Acquilix<sup>™</sup>
          </div>
        )}
      </div>
    </div>
  );

  const heroBg = style.hero_image_url
    ? `linear-gradient(160deg, rgba(42,24,16,0.72) 0%, rgba(36,26,17,0.85) 100%), url('${_abs(style.hero_image_url)}') center/cover no-repeat`
    : `linear-gradient(160deg, #2A1810 0%, ${accent} 100%)`;
  const heroHidden = !!style.hide_hero;

  return (
    <div style={{ minHeight: "100vh", background: `linear-gradient(180deg, ${bg} 0%, #FFFFFF 60%)`, fontFamily, color: text, "--eq-primary": primary }} data-testid="enq-widget-root">
      <style>{styles}</style>
      {!heroHidden && (
        <div style={{ background: heroBg, color: "white", padding: "72px 24px 96px", textAlign: "center" }} data-testid="enq-widget-hero">
          {style.logo_url && (
            <img src={_abs(style.logo_url)} alt="" loading="eager" decoding="async" style={{ maxHeight: 56, marginBottom: 20, filter: "brightness(0) invert(1)" }} />
          )}
          {style.show_workspace_name && (
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 4, opacity: 0.85, fontWeight: 600 }}>
              {cfg.workspace.name}
            </div>
          )}
          <h1 className="eq-hero-h1"
              style={{ fontFamily: serifFamily, fontSize: "clamp(30px, 5.5vw, 56px)", margin: "12px 0 0", fontWeight: 500, letterSpacing: -0.5 }}>
            {currentType ? `Enquire about ${currentType.name.toLowerCase()}` : "Tell us about your project"}
          </h1>
          <p style={{ maxWidth: 520, margin: "16px auto 0", fontSize: 15, opacity: 0.9, lineHeight: 1.55 }}>
            {style.tagline || "Share a few details and we'll come back with a tailored quote within one working day."}
          </p>
        </div>
      )}
      <div style={{ maxWidth: 720, margin: heroHidden ? "0 auto" : "-48px auto 0", padding: heroHidden ? "24px 18px 64px" : "20px 18px 96px" }}>
        <div className="eq-card" style={{ background: "white", padding: "28px 28px 40px", boxShadow: "0 24px 60px -20px rgba(36,26,17,0.18)", border: `1px solid ${cream200}`, borderTop: `2px solid ${primary}` }}>
          {/* Feb 2026 · R268nnn · Back-to-booking-home link — deep-links
              customers who landed here via a booking-widget tile back
              to the tile picker so they can swap intent without hitting
              the browser back button. Hidden when the widget is embedded
              (styleOverride flag from parent). */}
          {!styleOverride && wsId && (
            <a href={`/book/${wsId}`}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "6px 12px", background: "transparent",
                  border: `1px solid ${bark600}`, marginBottom: 20,
                  fontSize: 11, fontWeight: 700, letterSpacing: 1.5,
                  textTransform: "uppercase", textDecoration: "none",
                  color: bark800, transition: "background 120ms",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = cream100; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                data-testid="enq-back-to-home">
              ← Back to booking home
            </a>
          )}
          {style.welcome_html && (
            <div className="eq-welcome-html" style={{ marginBottom: 20, color: bark600 }}
                 dangerouslySetInnerHTML={{ __html: style.welcome_html }} />
          )}
          {!cfg.pinned && (
            <>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 3, color: bark600, fontWeight: 700, marginBottom: 14 }}>1 · What can we help with?</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12, marginBottom: 28 }}>
                {cfg.types.map((t) => (
                  <button key={t.id} type="button" onClick={() => setTypeId(t.id)}
                          data-testid={`enq-widget-type-${t.slug}`}
                          data-active={typeId === t.id ? "1" : "0"}
                          className="eq-type">
                    <div style={{ fontFamily: serifFamily, fontSize: 22, fontWeight: 500, color: text, letterSpacing: -0.3, lineHeight: 1.15 }}>{t.name}</div>
                    <div style={{ fontSize: 11, color: bark600, marginTop: 4 }}>{t.subtypes?.length || 0} option{t.subtypes?.length !== 1 ? "s" : ""}</div>
                  </button>
                ))}
              </div>
            </>
          )}
          {currentType && currentType.subtypes?.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 3, color: bark600, fontWeight: 700, marginBottom: 8 }}>{cfg.pinned ? "1" : "2"} · Which best fits?</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {currentType.subtypes.map((s) => (
                  <button key={s.slug} type="button" onClick={() => setForm({ ...form, subtype_slug: s.slug })}
                          data-testid={`enq-widget-sub-${s.slug}`}
                          style={{ padding: "8px 14px", background: form.subtype_slug === s.slug ? accent : "white",
                                      color: form.subtype_slug === s.slug ? "white" : text,
                                      border: `1px solid ${form.subtype_slug === s.slug ? accent : cream200}`,
                                      fontSize: 12, cursor: "pointer", fontWeight: 600 }}>{s.name}</button>
                ))}
              </div>
            </div>
          )}
          {typeId && (
            <>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 3, color: bark600, fontWeight: 700, marginBottom: 8 }}>{cfg.pinned ? "2" : "3"} · Your details</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                <input className="eq-input" placeholder="Full name*" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="enq-widget-name" />
                <input className="eq-input" placeholder="Email" type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="enq-widget-email" />
                <input className="eq-input" placeholder={style.require_phone ? "Phone*" : "Phone"} value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} data-testid="enq-widget-phone" />
                <input className="eq-input" placeholder={style.require_company ? "Company*" : "Company (optional)"} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="enq-widget-company" />
                <input className="eq-input" placeholder={style.require_budget ? "Budget*" : "Budget (optional)"} value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} data-testid="enq-widget-budget" />
              </div>
              <textarea className="eq-input" style={{ marginTop: 12, resize: "vertical" }} rows={4}
                         placeholder={style.require_message ? "Tell us about your project*" : "Tell us about your project (optional)"}
                         value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                         data-testid="enq-widget-message" />
              {style.terms_html && (
                <div className="eq-terms-html" style={{ marginTop: 16 }}
                     dangerouslySetInnerHTML={{ __html: style.terms_html }} />
              )}
              <div style={{ display: "flex", justifyContent: "center", marginTop: 24 }}>
                <button onClick={submit} disabled={busy}
                        style={{ padding: "16px 32px", background: accent, color: "white", border: 0,
                                    cursor: "pointer", width: "100%", maxWidth: 360,
                                    letterSpacing: 2, textTransform: "uppercase", fontWeight: 700, fontSize: 13,
                                    boxShadow: `0 12px 24px -8px ${accent}59` }}
                        data-testid="enq-widget-submit">
                  {busy ? "Sending…" : (style.cta_label || "Send enquiry →")}
                </button>
              </div>
            </>
          )}
        </div>
        {style.show_powered_by && (
          <div style={{ textAlign: "center", marginTop: 24, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", color: bark600, opacity: 0.7 }} data-testid="powered-by">
            Powered by{" "}
            <a href="https://acquilix.com" target="_blank" rel="noopener noreferrer"
                style={{ color: "inherit", textDecoration: "underline", letterSpacing: 3 }}>
              Acquilix<sup style={{ fontSize: "0.6em", fontWeight: 400, marginLeft: 1 }}>™</sup>
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function _stripEmpty(o) {
  const out = {};
  for (const k of Object.keys(o || {})) {
    const v = o[k];
    if (v === null || v === undefined) continue;
    if (typeof v === "string" && !v.trim()) continue;
    out[k] = v;
  }
  return out;
}
